import { useEffect, useMemo } from 'react';
import {
  ChevronDown,
  Package,
  FolderTree,
  Layers,
  ClipboardList,
  Warehouse,
  Truck,
  Settings,
  Tag,
  Heart,
  Sliders,
  Boxes,
  Smartphone,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { ProductListPage } from '@/pages/products/product-list';
import { DepartmentListPage } from '@/pages/products/department-list';
import { CategoryListPage } from '@/pages/products/category-list';
import { ModifierListPage } from '@/pages/products/modifier-list';
import { PromotionListPage } from '@/pages/products/promotion-list';
import { PurchaseOrderListPage } from '@/pages/purchasing/purchase-order-list';
import { WarehouseOrdersListPage } from '@/pages/purchasing/warehouse-orders-list';
import { WarehouseOutgoingListPage } from '@/pages/purchasing/warehouse-outgoing-list';
import { PackingConsolidatedPage } from '@/pages/purchasing/packing-consolidated';
import { PosConfigPage } from '@/pages/dashboard/pos-config-page';
import { MobileConfigPage } from '@/pages/dashboard/mobile-config-page';
import { FavoritesPage } from '@/pages/dashboard/favorites-page';
import { CustomerEngagementPage } from '@/pages/customer-engagement';
import { useAuthStore } from '@/stores/auth-store';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { useProductsPosConfig } from '@/hooks/use-pos-config';
import { usePermissions } from '@/hooks/use-permissions';
import type { PermissionKey } from '@supernova/shared';
import { useModules } from '@/hooks/use-modules';
import { usePersistentTab } from '@/hooks/use-persistent-tab';
import { useSidebarStore } from '@/stores/sidebar-store';
import { usePosNavStore } from '@/stores/pos-nav-store';
import { isWarehouseType } from '@/lib/location-type';

// ── Tab types ──
type Tab =
  | 'products'
  | 'departments'
  | 'categories'
  | 'modifiers'
  | 'promotions'
  | 'purchase-orders'
  | 'warehouse-orders'
  | 'warehouse-outgoing'
  | 'packing'
  | 'favorites'
  | 'customer-engagement'
  | 'pos-config'
  | 'mobile-config';

const VALID_TABS: Tab[] = [
  'products',
  'departments',
  'categories',
  'modifiers',
  'promotions',
  'purchase-orders',
  'warehouse-orders',
  'warehouse-outgoing',
  'packing',
  'favorites',
  'customer-engagement',
  'pos-config',
  'mobile-config',
];

// Which keys open each tab (any one of the list). Holding an action counts as
// holding its View. Tabs not listed (POS Settings, Mobile Settings) keep only
// their role gate.
const TAB_PERMISSIONS: Partial<Record<Tab, readonly PermissionKey[]>> = {
  products: ['products.view'],
  departments: ['departments.view'],
  categories: ['categories.view'],
  modifiers: ['modifiers.view'],
  promotions: ['promotions.view'],
  favorites: ['favorites.view'],
  'purchase-orders': ['purchase_orders.view'],
  // A warehouse's incoming orders are its sale orders.
  'warehouse-orders': ['sales_orders.view'],
  'warehouse-outgoing': ['sales_orders.view'],
  packing: ['sales_orders.packing'],
  'customer-engagement': ['customers.view', 'coupons.view', 'rewards.view', 'campaigns.view'],
};

// ── Main POS Management Page ──

export function PosManagementPage() {
  const { tenant } = useAuthStore();
  const isWarehouse = tenant?.type === 'warehouse';

  const permissions = usePermissions();
  // Mobile Settings stays with Owner and Admin by role, not a permission.
  const canManagePos = permissions.atLeast('admin');
  // POS Settings stays owner-only (plus platform super admins) by role.
  const canManagePosConfig = permissions.atLeast('owner');
  const permitsTab = (key: Tab) => {
    const needed = TAB_PERMISSIONS[key];
    return !needed || permissions.hasAny(...needed);
  };

  // Show "Sale Orders" tab only when the *currently selected* location is a
  // warehouse. Sale orders are POs the warehouse fulfills as the seller — they
  // belong to that location's view. Showing the tab on a non-warehouse store
  // confused testers because clicking it surfaced rows that don't apply to
  // the store they're standing in. Standalone warehouse tenants already get
  // the dedicated "Warehouse Orders" tab.
  const { data: locationsData } = useLocations();
  const { selectedLocationId } = useLocationStore();
  const selectedLocationIsWarehouse = useMemo(() => {
    if (isWarehouse) return false;
    const loc = (locationsData?.data ?? []).find((l: any) => l.id === selectedLocationId);
    return !!loc && isWarehouseType(loc.type) && loc.isActive;
  }, [isWarehouse, locationsData, selectedLocationId]);

  // Per-location runtime toggle for the Customer Engagement tab.
  // Super admin flips it from POS Management → POS Config → Customer Engagement.
  // Default is false at every location until explicitly enabled.
  const { data: posConfig } = useProductsPosConfig(selectedLocationId);
  const customerEngagementEnabled = posConfig?.customerEngagementEnabled === true;
  const promotionsEnabled = posConfig?.promotionsEnabled === true;

  // Packing tab: consolidated PO view for the warehouse packing team. Gated
  // per (tenant, warehouse location) by the `warehouse_packing` module, which
  // only a super admin can open — there is no merchant-side isActive step, so
  // the gate flag alone decides visibility.
  const { data: modulesData } = useModules(selectedLocationId);
  const packingEnabled =
    selectedLocationIsWarehouse &&
    (modulesData?.data ?? []).some((m) => m.moduleName === 'warehouse_packing' && m.superAdminEnabled);

  const [activeTab, setActiveTab] = usePersistentTab<Tab>(VALID_TABS, 'products');

  // A previously-persisted tab the role or permissions no longer cover (e.g.
  // a saved tab from before their role changed) moves to the first one they
  // have, so they don't sit on a hidden tab with a blank content area.
  const roleBlocksTab =
    (!canManagePos && activeTab === 'mobile-config') || (!canManagePosConfig && activeTab === 'pos-config');
  useEffect(() => {
    if (!roleBlocksTab && permitsTab(activeTab)) return;
    const first = VALID_TABS.find((t) => TAB_PERMISSIONS[t] && permitsTab(t));
    if (first) setActiveTab(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, roleBlocksTab, activeTab, setActiveTab]);

  const tabs = useMemo<{ key: Tab; label: string; icon: React.ElementType }[]>(
    () => [
      { key: 'products', label: 'Products', icon: Package },
      { key: 'departments', label: 'Departments', icon: FolderTree },
      { key: 'categories', label: 'Categories', icon: Layers },
      { key: 'modifiers', label: 'Modifiers', icon: Sliders },
      // Promotions tab is per-location super-admin gated via
      // location_pos_config.promotions_enabled (same model as Customer
      // Engagement). The tab disappears entirely when the toggle is off.
      ...(promotionsEnabled ? [{ key: 'promotions' as Tab, label: 'Promotions', icon: Tag }] : []),
      // Central favorites: tenant-wide + per-location tiles pushed to every
      // register, so they survive reinstall and are managed centrally.
      { key: 'favorites', label: 'Favorites', icon: Heart },
      { key: 'purchase-orders', label: 'Purchase Orders', icon: ClipboardList },
      // Only show warehouse orders tab for standalone warehouse tenants
      ...(isWarehouse ? [{ key: 'warehouse-orders' as Tab, label: 'Warehouse Orders', icon: Warehouse }] : []),
      // Sale Orders tab is location-scoped: only shown when the user is
      // currently inside a warehouse location.
      ...(selectedLocationIsWarehouse ? [{ key: 'warehouse-outgoing' as Tab, label: 'Sale Orders', icon: Truck }] : []),
      // Packing (consolidated PO) is only offered inside a warehouse location
      // and only once the super admin opens the warehouse_packing gate for it.
      ...(packingEnabled ? [{ key: 'packing' as Tab, label: 'Packing', icon: Boxes }] : []),
      // Customer Engagement is gated per-location by a super-admin toggle stored
      // on location_pos_config.customer_engagement_enabled. The tab disappears
      // entirely when the toggle is off (or no location is selected yet).
      // Opens with any of its sections' View keys.
      ...(customerEngagementEnabled
        ? [{ key: 'customer-engagement' as Tab, label: 'Customer Management', icon: Heart }]
        : []),
      // POS Config is restricted to Owner only (see canManagePosConfig).
      ...(canManagePosConfig ? [{ key: 'pos-config' as Tab, label: 'POS Settings', icon: Settings }] : []),
      // Mobile Config decides which pages store staff can open on the phone and
      // which restricted fields they see there. Owner and Admin (see
      // canManagePos) — a wider gate than POS Config, which stays owner-only,
      // and it matches the admin+ role guard on the write route upstream.
      ...(canManagePos ? [{ key: 'mobile-config' as Tab, label: 'Mobile Settings', icon: Smartphone }] : []),
    ],
    [
      promotionsEnabled,
      isWarehouse,
      selectedLocationIsWarehouse,
      packingEnabled,
      customerEngagementEnabled,
      canManagePos,
      canManagePosConfig,
    ],
  );

  // Drop the tabs the user's permissions don't cover.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleTabs = useMemo(() => tabs.filter((t) => permitsTab(t.key)), [tabs, permissions]);
  const shownTab = permitsTab(activeTab) ? activeTab : null;

  // Publish the sections to the sidebar, which shows them in place of the
  // main menu while the user is in POS Management (see PosSidebarNav). On
  // phones the same list opens in the navigation drawer.
  const registerNav = usePosNavStore((s) => s.register);
  useEffect(() => {
    registerNav(visibleTabs, activeTab);
  }, [registerNav, visibleTabs, activeTab]);
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const current = visibleTabs.find((t) => t.key === activeTab);
  const CurrentIcon = current?.icon;

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader title="POS Management" description="Manage your point-of-sale">
        <LocationBadge />
      </PageHeader>

      {/* Below the desktop breakpoint the sections live in the navigation
          drawer: this names the open one and opens the drawer to switch. On
          desktop the sidebar shows them permanently, so it is hidden there. */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted lg:hidden"
      >
        <span className="flex items-center gap-2.5">
          {CurrentIcon && <CurrentIcon className="h-5 w-5 text-primary" />}
          {current?.label ?? 'Section'}
        </span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {visibleTabs.length} sections
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {/* Tab content */}
      {shownTab === 'products' && <ProductListPage />}
      {shownTab === 'departments' && <DepartmentListPage />}
      {shownTab === 'categories' && <CategoryListPage />}
      {shownTab === 'modifiers' && <ModifierListPage />}
      {shownTab === 'promotions' && promotionsEnabled && <PromotionListPage />}
      {shownTab === 'purchase-orders' && <PurchaseOrderListPage />}
      {shownTab === 'warehouse-orders' && isWarehouse && <WarehouseOrdersListPage />}
      {shownTab === 'warehouse-outgoing' && selectedLocationIsWarehouse && <WarehouseOutgoingListPage />}
      {shownTab === 'packing' && packingEnabled && <PackingConsolidatedPage />}
      {shownTab === 'customer-engagement' && customerEngagementEnabled && <CustomerEngagementPage />}
      {shownTab === 'favorites' && <FavoritesPage />}
      {shownTab === 'pos-config' && canManagePosConfig && <PosConfigPage />}
      {shownTab === 'mobile-config' && canManagePos && <MobileConfigPage />}
    </div>
  );
}
